import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { firestore as db } from "../FirebaseConfig";
import ProductCard from "./listOfProducts/ProductCard";
import { useSearchParams } from "react-router-dom";
import Lottie from "lottie-react";
import { FiSearch, FiFilter } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import HeroSlider from "./BannerPage";
import BeeScrollProgress from "../Home_Page/components/BeeScrollProgress";



const Shop = () => {
  // --- State ---
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentBanner, setCurrentBanner] = useState("");
  
  // Color state
  const [headerColor, setHeaderColor] = useState("#f57c00");

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState("Featured");

  const [loading, setLoading] = useState(true);
  const [animationData, setAnimationData] = useState(null);
  const [searchParams] = useSearchParams();

  // Sort Categories
  const availableCategories = useMemo(
    () =>
      categories
        .filter((cat) => cat.availability)
        .sort((a, b) => (a.index || 0) - (b.index || 0)),
    [categories]
  );

  // --- Initial Data Fetch ---
  useEffect(() => {
    window.scrollTo(0, 0);
    const query = searchParams.get("search");
    if (query) setSearchQuery(query);

    const fetchData = async () => {
      try {
        const catSnap = await getDocs(collection(db, "categories"));
        const catList = catSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCategories(catList);

        const prodSnap = await getDocs(collection(db, "products"));
        const prodList = prodSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const activeProducts = prodList.filter((p) => p.main === true);
        setProducts(activeProducts);

        // --- NEW: PRELOAD BANNERS ---
        // This forces the browser to download all category images into its cache
        // effectively making subsequent clicks "instant".
        catList.forEach((cat) => {
          const imgUrl = cat.banner || cat.image;
          if (imgUrl) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = imgUrl;
            document.head.appendChild(link);
            
            // Fallback for older browsers:
            new Image().src = imgUrl; 
          }
        });

      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    fetch("/assets/General/loadingBee.json")
      .then((res) => res.json())
      .then((data) => setAnimationData(data))
      .catch(() => {});
  }, [searchParams]);

  // --- Banner & Header Logic ---
  useEffect(() => {
    if (selectedCategory === "all") {
      // Logic for ALL
      setCurrentBanner(""); // Empty string triggers your gradient overlay
      setHeaderColor("#f57c00");
    } else {
      const categoryData = categories.find((c) => c.id === selectedCategory);
      if (categoryData) {
        // Set the Banner
        setCurrentBanner(categoryData.banner || categoryData.image || "");
        setHeaderColor(categoryData.color || "#f57c00");
      }
    }
  }, [selectedCategory, categories]);

  // --- Filtering & Sorting ---
  // const filteredProducts = useMemo(() => {
  //   let updated = [...products];

  //   if (selectedCategory !== "all") {
  //     updated = updated.filter((p) => p.categoryId === selectedCategory);
  //   }
  //   if (searchQuery.trim()) {
  //     updated = updated.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
  //   }

  //   switch (sortOption) {
  //     case "Price: Low to High":
  //       updated.sort((a, b) => (a.prices.productPrice - a.prices.productDiscount) - (b.prices.productPrice - b.prices.productDiscount));
  //       break;
  //     case "Price: High to Low":
  //       updated.sort((a, b) => (b.prices.productPrice - b.prices.productDiscount) - (a.prices.productPrice - a.prices.productDiscount));
  //       break;
  //     case "Avg. Rating":
  //       updated.sort((a, b) => (b.rating || 0) - (a.rating || 0));
  //       break;
  //     default: break; 
  //   }

  //   updated.sort((a, b) => {
  //     const aAvailable = a.inStock === true || a.stock > 0;
  //     const bAvailable = b.inStock === true || b.stock > 0;
  //     if (aAvailable === bAvailable) return 0;
  //     return aAvailable ? -1 : 1; 
  //   });

  //   return updated;
  // }, [products, selectedCategory, searchQuery, sortOption]);


  // --- Filtering & Sorting ---
  const filteredProducts = useMemo(() => {
    let updated = [...products];

    // 1. Filter by Category
    if (selectedCategory !== "all") {
      updated = updated.filter((p) => p.categoryId === selectedCategory);
    }
    
    // 2. Filter by Search
    if (searchQuery.trim()) {
      updated = updated.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // 3. APPLY GLOBAL SORTING
    updated.sort((a, b) => {
      // --- A. CATEGORY ORDER (The Priority) ---
      // We find the index of the category each product belongs to
      const catA = categories.find(c => c.id === a.categoryId);
      const catB = categories.find(c => c.id === b.categoryId);
      
      const indexA = catA?.index ?? 999;
      const indexB = catB?.index ?? 999;

      if (indexA !== indexB) {
        return indexA - indexB;
      }

      // --- B. USER SELECTED SORT (Price, Rating, etc.) ---
      // This happens ONLY if they are in the same category
      if (sortOption === "Price: Low to High") {
        return (a.prices.productPrice - a.prices.productDiscount) - (b.prices.productPrice - b.prices.productDiscount);
      }
      if (sortOption === "Price: High to Low") {
        return (b.prices.productPrice - b.prices.productDiscount) - (a.prices.productPrice - a.prices.productDiscount);
      }
      if (sortOption === "Avg. Rating") {
        return (b.rating || 0) - (a.rating || 0);
      }

      // --- C. STOCK AVAILABILITY (Last priority) ---
      const aAvailable = a.stock > 0;
      const bAvailable = b.stock > 0;
      if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;

      return 0;
    });

    return updated;
  }, [products, selectedCategory, searchQuery, sortOption, categories]);

  // --- Sync URL Params ---
  useEffect(() => {
    if (categories.length === 0) return;
    const categorySlug = searchParams.get("category");
    if (categorySlug) {
      const slugs = categorySlug.split("--");
      const matched = categories.filter((cat) => slugs.includes(cat.slug));
      if (matched.length > 0) setSelectedCategory(matched[0].id);
    }
  }, [categories, searchParams]);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center bg-gray-50">
        <div className="w-1/2 md:w-1/4">
          {animationData && <Lottie animationData={animationData} loop autoPlay />}
        </div>
      </div>
    );
  }

  const renderCategoryButton = (catId, iconSrc, label, isActive, onClick) => (
    <button
      onClick={onClick}
      className={`group flex flex-col items-center justify-start flex-shrink-0 min-w-[60px]`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 border-2 ${
        isActive ? "bg-white border-white scale-110 shadow-xl" : "bg-white/15 border-white/40 hover:bg-white/25"
      }`}>
        <img src={iconSrc} className={`w-8 h-8 object-contain transition-all ${isActive ? "" : "brightness-0 invert"}`} alt={label} />
      </div>
      <span className={`mt-2 text-xs font-medium text-center max-w-[80px] truncate ${isActive ? "text-white" : "text-white/75"}`}>
        {label}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
            <BeeScrollProgress/>

      {/* HEADER SECTION */}
      <motion.div
        className="sticky top-26 shadow-lg rounded-b-[2rem] pt-4"
        style={{ backgroundImage: `url('/assets/shop_page/grad_bg.jpg')` }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4">
          
          {/* SEARCH BAR */}
          <div className="w-full max-w-2xl mx-auto mb-4">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for 'Honey'..."
                className="w-full pl-11 pr-12 py-3 rounded-2xl bg-white text-gray-800 placeholder-gray-500 focus:outline-none shadow-sm"
              />
              <button onClick={() => setShowFilters((prev) => !prev)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-xl transition">
                <FiFilter className={`text-lg ${showFilters ? "text-purple-600" : "text-gray-500"}`} />
              </button>
            </div>
          </div>

          {/* CATEGORIES (Mobile + Desktop) */}
          <div className="w-full md:hidden mb-3">
            <div className="flex overflow-x-auto hide-scrollbar gap-3 items-start px-1 pb-1">
              {renderCategoryButton("all", "/assets/shop_page/all_honey.png", "All", selectedCategory === "all", () => setSelectedCategory("all"))}
              {availableCategories.map((cat) =>
                renderCategoryButton(cat.id, cat.icon, cat.name.replace("Honey", ""), selectedCategory === cat.id, () => setSelectedCategory(cat.id))
              )}
            </div>
          </div>
          <div className="hidden md:block mb-4">
            <div className="grid grid-cols-6 lg:grid-cols-8 gap-3">
              <div className="flex justify-center">
                {renderCategoryButton("all", "/assets/shop_page/all_honey.png", "All", selectedCategory === "all", () => setSelectedCategory("all"))}
              </div>
              {availableCategories.map((cat) => (
                <div key={cat.id} className="flex justify-center hide-scrollbar">
                  {renderCategoryButton(cat.id, cat.icon, cat.name.replace("Honey", ""), selectedCategory === cat.id, () => setSelectedCategory(cat.id))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* --- D. BANNER AREA --- */}
        <div className="container mx-auto px-4 mt-2 pb-6">
          <motion.div
            layout
            className="relative w-full lg:max-w-5xl mx-auto aspect-[3/1] rounded-2xl overflow-hidden shadow-lg bg-gray-900"
          >
            {/* 1. Image Layer: Uses the NEW BannerImage component */}
           
<HeroSlider selectedCategory={selectedCategory==="all"?"all":categories.find((cat) => cat.id === selectedCategory)?.slug} />
          

            {/* 2. Text Overlay: Shows on "All" OR if user wants text over image */}
      
          </motion.div>
        </div>
      </motion.div>

      {/* --- PRODUCT LIST --- */}
      <div className="container mt-26 mx-auto px-4 pt-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
          <p className="text-gray-500 text-sm font-semibold">{filteredProducts.length} Products Found</p>
          
          <div>
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="mt-3 bg-white rounded-2xl shadow-lg p-3 flex flex-col gap-2 md:flex-row md:items-center"
                >
                  <div className="text-sm text-gray-600 font-semibold">Sort by:</div>
                  <div className="flex gap-2 flex-wrap">
                    {["Featured", "Price: Low to High", "Price: High to Low", "Avg. Rating"].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSortOption(opt)}
                        className={`px-3 py-1.5 rounded-full text-xs md:text-sm border transition ${
                          sortOption === opt ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} data={product} />
          ))}
        </div>
        
        {filteredProducts.length === 0 && (
           <div className="flex flex-col items-center justify-center py-20 text-gray-500">
             <img src="/assets/empty_cart.png" className="w-32 opacity-50 mb-4" alt="Empty" />
             <p>No products match your search.</p>
           </div>
        )}
      </div>
    </div>
  );
};

export default Shop;